import type { SxProps, Theme } from "@mui/material";
import { Util } from "../../util/Util";
import { compareToBusinessToday } from "../../util/businessClock";

export const isSupplyReturn = (row: { batch?: string; sup_type?: string }) =>
	Boolean(row.batch?.startsWith("ret:") || Util.compareStrings(row.sup_type || "", "Return"));

export const canEditSupply = (row: { batch?: string; sup_type?: string }) =>
	!isSupplyReturn(row);

export const canDeleteSupply = (row: {
	batch?: string;
	sup_type?: string;
	paid?: number;
}) => {
	if (isSupplyReturn(row) && Number(row.paid) !== 0) {
		return false;
	}
	return true;
};

export const parseSupplyExpiry = (value: Date | string | null | undefined): Date | null => {
	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : value;
	}
	if (typeof value === "string" && value.trim()) {
		const parsed = new Date(value);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	}
	return null;
};

export const formatSupplyExpiry = (value: Date | string | null | undefined): string | null => {
	const parsed = parseSupplyExpiry(value);
	return parsed ? Util.formatISODate(parsed) : null;
};

export const isDueDateValidForSupply = (
	supplyDate?: string | null,
	dueDate?: string | null,
) => !dueDate || !supplyDate || dueDate >= supplyDate;

export const isSupplyOverdue = (row: { balance?: number; due_date?: string | null }) => {
	if (!row.due_date) return false;

	const balance = Number(row.balance);
	if (!Number.isFinite(balance) || balance === 0) return false;

	// Calendar comparison against the server's business day.
	const due = String(row.due_date).slice(0, 10);
	if (!/^\d{4}-\d{2}-\d{2}$/.test(due)) return false;

	return compareToBusinessToday(due) < 0;
};

export const getSupplyRowSx = (
	row: { batch?: string; sup_type?: string; balance?: number; due_date?: string | null },
	theme: Theme,
): SxProps<Theme> => {
	if (isSupplyReturn(row)) {
		return {
			backgroundColor:
				theme.palette.mode === "dark"
					? "rgba(255, 167, 38, 0.12)"
					: "rgba(255, 167, 38, 0.08)",
			borderLeft: "3px solid",
			borderLeftColor: "warning.main",
		};
	}

	if (isSupplyOverdue(row)) {
		return {
			backgroundColor:
				theme.palette.mode === "dark"
					? "rgba(244, 67, 54, 0.12)"
					: "rgba(244, 67, 54, 0.08)",
			borderLeft: "3px solid",
			borderLeftColor: "error.main",
		};
	}

	return {};
};